import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, inject, input, output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { VivestInputValueAccessorDirective } from '../../directives/input-vivest/vivest-input-value-accessor.directive';
import { VivestAutocompleteValueAccessorDirective } from '../../directives/select-vivest/vivest-autocomplete-value-accessor.directive';
import { VivestListaVisivelDirective } from '../../directives/select-vivest/vivest-lista-visivel.directive';
import { VivestSelectValueAccessorDirective } from '../../directives/select-vivest/vivest-select-value-accessor.directive';

/**
 * Supported field types for the generic filter bar. `text` and `select` are native controls;
 * `vivest-input`, `vivest-select` and `vivest-autocomplete` are the design system components (the
 * autocomplete value is `''` until an option is chosen). The design system has no date input, so
 * `date` and `daterange` stay native.
 */
export type FilterFieldType =
  | 'text'
  | 'select'
  | 'vivest-input'
  | 'vivest-select'
  | 'vivest-autocomplete'
  | 'date'
  | 'daterange';

/** One option inside a `select` field. */
export interface FilterFieldOption {
  value: string;
  label: string;
}

/**
 * Descriptor for a single filter field rendered by `FilterBarComponent`.
 *
 * - `key`: FormControl name (for `daterange`, the "from" control).
 * - `keyTo`: FormControl name for the "to" control of a `daterange` field.
 * - `defaultValue`: Initial value; defaults to `''` when omitted.
 * - `clearValue`: Value restored by the "Limpar" button; defaults to `defaultValue` when
 *   omitted. Use when the initial landing filter differs from "no filter" (ex.: status
 *   defaults to a specific value on load, but Limpar should go back to "Todos").
 */
export interface FilterField {
  key: string;
  label: string;
  type: FilterFieldType;
  placeholder?: string;
  options?: FilterFieldOption[];
  defaultValue?: string;
  clearValue?: string;
  keyTo?: string;
}

@Component({
  selector: 'app-filter-bar',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    VivestInputValueAccessorDirective,
    VivestSelectValueAccessorDirective,
    VivestAutocompleteValueAccessorDirective,
    VivestListaVisivelDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss',
})
export class FilterBarComponent implements OnInit {
  private readonly fb = inject(FormBuilder);

  /**
   * Field descriptors that drive the rendered form controls.
   * At minimum provide `key`, `label`, and `type`.
   */
  readonly fields = input.required<readonly FilterField[]>();

  /** Emits the full form value as a flat record whenever the user submits or clears. */
  readonly filtersChange = output<Record<string, string>>();

  form!: FormGroup;

  ngOnInit(): void {
    this.buildForm();
    this.emitCurrent();
  }

  /** Submits the current form values. */
  buscar(): void {
    this.emitCurrent();
  }

  /**
   * Resets every control to its `clearValue` (or `defaultValue`, or `''`) and emits the cleared
   * values, so the list goes back to showing everything.
   */
  limpar(): void {
    const defaults = this.buildDefaults((field) => field.clearValue ?? field.defaultValue);
    this.form.reset(defaults);
    this.emitCurrent();
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  private buildForm(): void {
    const controls: Record<string, string> = this.buildDefaults((field) => field.defaultValue);
    this.form = this.fb.nonNullable.group(controls);
  }

  private buildDefaults(resolveValue: (field: FilterField) => string | undefined): Record<string, string> {
    const defaults: Record<string, string> = {};
    for (const field of this.fields()) {
      defaults[field.key] = resolveValue(field) ?? '';
      if (field.type === 'daterange' && field.keyTo) {
        defaults[field.keyTo] = '';
      }
    }
    return defaults;
  }

  private emitCurrent(): void {
    this.filtersChange.emit(this.form.getRawValue() as Record<string, string>);
  }
}
